import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  StatusBar,
  Modal,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  Typography,
  chefPlating,
} from '@weareopenr/component-library';
import { useForm } from 'react-hook-form';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import type { IMenuItem } from 'src/types/menu.types';
import styles from './CreateOptionGroupModal.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import { IOptionGroupPayload } from '../../api/updateModifierGroup/types';
import { formatCreateOptionGroupRequest } from './utilities';
import { createModifierGroup } from '../../api/createModifierGroup/createModifierGroup';
import { QueryKeys } from '../../hooks/QueryKeys';
import { SetPublicationExternalSystems } from './PublicationChannels/SetPublicationExternalSystems';
import { useExternalSystems } from '../../hooks/useExternalSystems';
import { OptionGroupDetails } from './OptionGroupDetails/OptionGroupDetails';
import { IOptionGroupValues } from './types';
import { OptionGroupPricing } from '../OptionGroupPricing';
import { useSelectedPriceScheme } from '../../hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { downStreamExternalSystemIntegrationTypes, EntitySource, ItemType } from '../../constants';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';

interface IModalProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemType: ItemType.BUNDLE | ItemType.STANDARD;
  bundleRangeIds: string[];
}

enum FlowSteps {
  Details = 0,
  Pricing = 1,
  externalSystems = 2,
}

const flowLength = {
  [ItemType.BUNDLE]: 1,
  [ItemType.STANDARD]: 2,
  [ItemType.NON_FOOD]: 2,
  noExternalSystems: 1,
};

const getDefaultSource = (itemType: ItemType, bundleRangeIds: string[]): { ranges: string[]; source: EntitySource } => {
  if (itemType === ItemType.BUNDLE && bundleRangeIds.length > 0) {
    return {
      ranges: bundleRangeIds,
      source: EntitySource.RANGES,
    };
  }
  return {
    ranges: [],
    source: EntitySource.LIBRARY,
  };
};

const CreateOptionGroupForm = ({ itemId, onClose, itemType, bundleRangeIds }: Omit<IModalProps, 'open'>) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(FlowSteps.Details);

  const [finalStep, setFinalStep] = useState<number>(flowLength[itemType]);
  const { selectedPriceSchemeId } = useSelectedPriceScheme();

  const { data: externalSystems } = useExternalSystems();
  useEffect(() => {
    if (externalSystems) {
      const downstreamExternalSystems = externalSystems.filter((externalSystem) =>
        downStreamExternalSystemIntegrationTypes.includes(externalSystem.integrationType),
      );
      if (downstreamExternalSystems.length === 0 && itemType === ItemType.STANDARD) {
        setFinalStep(flowLength.noExternalSystems);
      }
    }
  }, [externalSystems]);

  const nextStep = async () => {
    const valid = await trigger();
    if (valid) {
      setStep((prev) => prev + 1);
    }
  };

  const previousStep = () => {
    setStep((prev) => prev - 1);
  };

  const defaultValues: IOptionGroupValues = useMemo(() => {
    return {
      name: '',
      consumer_name: '',
      description: '',
      min_choices: 0,
      max_choices: 1,
      uniqueOnly: true,
      externalSystems: [],
      modifiers: [],
      ...getDefaultSource(itemType, bundleRangeIds),
    };
  }, []);

  const useFormReturn = useForm<IOptionGroupValues>({ defaultValues, mode: 'onChange' });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    watch,
    trigger,
    setValue,
  } = useFormReturn;

  const extSystems = watch('externalSystems');
  const { errorMessage, showNotification, clearError } = useModalNotification();

  const submitHandler = async (data: IOptionGroupValues) => {
    clearError();
    try {
      const payload: IOptionGroupPayload = formatCreateOptionGroupRequest(data);
      await createModifierGroup(itemId, payload, selectedPriceSchemeId);

      await queryClient.invalidateQueries<IMenuItem>([QueryKeys.item, itemId]);
      onClose();
      showNotification({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully created ${data.name}`,
      });
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: `Something went wrong whilst creating ${data.name}`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Typography size="small" className={styles['step']}>
        {`Step ${step + 1} of ${finalStep + 1}`}
      </Typography>
      {step === FlowSteps.Details && <OptionGroupDetails useFormReturn={useFormReturn} itemType={itemType} />}
      {step === FlowSteps.Pricing && <OptionGroupPricing formReturn={useFormReturn} />}
      {step === FlowSteps.externalSystems && (
        <SetPublicationExternalSystems
          externalSystems={externalSystems}
          selectedExternalSystems={extSystems}
          setValue={setValue}
        />
      )}
      <ButtonContainer
        left={
          <SecondaryButton
            size="full-width"
            type="button"
            buttonStyle="outline"
            onClick={step === FlowSteps.Details ? onClose : previousStep}
          >
            {step === FlowSteps.Details ? 'Close' : 'Back'}
          </SecondaryButton>
        }
        right={
          step === finalStep ? (
            <Fragment key="create-button">
              <PrimaryButton loading={isSubmitting} size="full-width" type="submit" disabled={!isValid}>
                Create
              </PrimaryButton>
            </Fragment>
          ) : (
            <Fragment key="next-button">
              <PrimaryButton size="full-width" type="button" onClick={nextStep} disabled={!isValid}>
                Next
              </PrimaryButton>
            </Fragment>
          )
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </form>
  );
};

export const CreateOptionGroupModal = ({ open, onClose, itemId, itemType, bundleRangeIds }: IModalProps) => {
  return (
    <Modal image={chefPlating} closeModal={onClose} open={open}>
      <CreateOptionGroupForm itemId={itemId} onClose={onClose} itemType={itemType} bundleRangeIds={bundleRangeIds} />
    </Modal>
  );
};

export default CreateOptionGroupModal;
